import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import usePermission from "../../hooks/usePermission";
import { PERMISSIONS } from "../../adminConfig";

import group1 from "../../assets/Group 1.svg";
import User1 from "../../assets/User1.svg";
import notification from "../../assets/notification-bell1394.svg";
import vectorC from "../../assets/Vector copy.svg";
import DAW from "../../assets/VectorDAW.svg";
import DAB from "../../assets/VectorDAB.svg";
import contentB from "../../assets/Group 1ContentB.svg";
import notifiB from "../../assets/NBB.svg";
import appManager from "../../assets/Icon/setting.svg";

const APPS = {
  LXDC: {
    label: "LXDC",
    links: [
      { to: "/",                 label: "Data Analysis",    iconOff: DAW,         iconOn: DAB,         permission: PERMISSIONS.VIEW_ANALYTICS   },
      { to: "/user",             label: "User",             iconOff: vectorC,     iconOn: User1,       permission: PERMISSIONS.READ_USERS        },
      { to: "/content",          label: "Content",          iconOff: group1,      iconOn: contentB,    permission: PERMISSIONS.MANAGE_CONTENT    },
      { to: "/notifications",    label: "Notification",     iconOff: notification,iconOn: notifiB,     permission: PERMISSIONS.VIEW_ANALYTICS    },
      { to: "/appmanager",       label: "App Manager",      iconOff: appManager,  iconOn: appManager,  permission: PERMISSIONS.MANAGE_SYSTEM     },
      { to: "/email",            label: "Email",            iconOff: appManager,  iconOn: appManager,  permission: PERMISSIONS.MANAGE_CAMPAIGNS  },
      { to: "/admin-management", label: "Admin Management", iconOff: appManager,  iconOn: appManager,  permission: PERMISSIONS.MANAGE_SYSTEM     },
      { to: "/settings",         label: "Settings",         iconOff: appManager,  iconOn: appManager,  permission: PERMISSIONS.MANAGE_SYSTEM     },
    ],
  },
  VORTEX: {
    label: "Vortex",
    links: [
      { to: "/vortexData",           label: "Data Analysis",    iconOff: DAW,        iconOn: DAB,        permission: PERMISSIONS.VIEW_ANALYTICS   },
      { to: "/vortexusers",          label: "Users",            iconOff: vectorC,    iconOn: User1,      permission: PERMISSIONS.READ_USERS        },
      { to: "/vortexemail",          label: "Email",            iconOff: group1,     iconOn: contentB,   permission: PERMISSIONS.MANAGE_CAMPAIGNS  },
      { to: "/vortexnotifications",  label: "Notifications",    iconOff: appManager, iconOn: appManager, permission: PERMISSIONS.VIEW_ANALYTICS    },
      { to: "/vortexmatches",        label: "Matches",          iconOff: appManager, iconOn: appManager, permission: PERMISSIONS.MANAGE_SYSTEM     },
      { to: "/vortexappmanager",     label: "App Manager",      iconOff: appManager, iconOn: appManager, permission: PERMISSIONS.MANAGE_SYSTEM     },
      { to: "/admin-management",     label: "Admin Management", iconOff: appManager, iconOn: appManager, permission: PERMISSIONS.MANAGE_SYSTEM     },
      { to: "/vortexsettings",       label: "Settings",         iconOff: appManager, iconOn: appManager, permission: PERMISSIONS.MANAGE_SYSTEM     },
    ],
  },
};

const SIDEBAR_WIDTH = 272;

const Sidebar = ({ mobileOpen = false, setMobileOpen = () => {} }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { can } = usePermission();
  const dropRef = useRef(null);
  const dropBtnRef = useRef(null);

  const [open, setOpen] = useState(true);
  const [activeApp, setActiveApp] = useState(
    location.pathname.startsWith("/vortex") ? "VORTEX" : "LXDC"
  );
  const [activeLink, setActiveLink] = useState(location.pathname);
  const [dropOpen, setDropOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 640);
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, width: 0 });

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 640);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Auto-switch active app when URL changes (e.g. direct navigation or back/forward)
  useEffect(() => {
    setActiveApp(location.pathname.startsWith("/vortex") ? "VORTEX" : "LXDC");
    setActiveLink(location.pathname);
  }, [location.pathname]);

  // ✅ Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (
        dropRef.current && !dropRef.current.contains(e.target) &&
        dropBtnRef.current && !dropBtnRef.current.contains(e.target)
      ) {
        setDropOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ✅ Calculate dropdown position from button position
  const handleDropToggle = () => {
    if (!dropOpen && dropBtnRef.current) {
      const rect = dropBtnRef.current.getBoundingClientRect();
      setDropPos({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
    setDropOpen(!dropOpen);
  };

  const handleAppSwitch = (appKey) => {
    setActiveApp(appKey);
    setDropOpen(false);
    const firstRoute = APPS[appKey].links[0].to;
    navigate(firstRoute);
    setActiveLink(firstRoute);
    setMobileOpen(false);
  };

  const currentLinks = APPS[activeApp].links.filter((l) => can(l.permission));

  // ✅ Portal dropdown — renders at document.body level, escapes ALL stacking contexts
  const PortalDropdown = () => {
    if (!dropOpen) return null;
    return ReactDOM.createPortal(
      <div
        ref={dropRef}
        style={{
          position: "fixed",
          top: dropPos.top,
          left: dropPos.left,
          width: dropPos.width,
          backgroundColor: "white",
          color: "black",
          borderRadius: "6px",
          boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
          zIndex: 99999,
        }}
      >
        {Object.entries(APPS).map(([key, app]) => (
          <button
            key={key}
            onClick={() => handleAppSwitch(key)}
            style={{
              width: "100%",
              textAlign: "left",
              padding: "10px 12px",
              background: key === activeApp ? "#f3f4f6" : "none",
              border: "none",
              cursor: "pointer",
              fontSize: "14px",
              color: "black",
              borderRadius: "6px",
              fontWeight: key === activeApp ? 600 : 400,
            }}
          >
            {app.label}
          </button>
        ))}
      </div>,
      document.body
    );
  };

  const AppSwitcherButton = () => (
    <div style={{ padding: "12px" }}>
      <button
        ref={dropBtnRef}
        onClick={handleDropToggle}
        style={{
          width: "100%",
          backgroundColor: "white",
          color: "black",
          padding: "8px 12px",
          borderRadius: "6px",
          border: "none",
          cursor: "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontWeight: 500,
          fontSize: "14px",
        }}
      >
        {APPS[activeApp].label}
        <span style={{ fontSize: "12px" }}>{dropOpen ? "▲" : "▼"}</span>
      </button>
    </div>
  );

  const NavLinks = ({ showLabel }) => (
    <nav style={{
      display: "flex",
      flexDirection: "column",
      gap: "4px",
      padding: "8px",
      flex: 1,
      overflowY: "auto",
    }}>
      {currentLinks.map(({ to, label, iconOff, iconOn }) => {
        const isActive = activeLink === to;
        return (
          <Link
            key={to}
            to={to}
            title={!showLabel ? label : ""}
            onClick={() => {
              setActiveLink(to);
              setMobileOpen(false);
              setDropOpen(false);
            }}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: showLabel ? "flex-start" : "center",
              gap: "12px",
              padding: "10px 12px",
              borderRadius: "6px",
              textDecoration: "none",
              backgroundColor: isActive ? "white" : "transparent",
              color: isActive ? "black" : "white",
              fontWeight: isActive ? 600 : 400,
              transition: "background 0.2s",
            }}
          >
            <img
              src={isActive ? iconOn : iconOff}
              alt={label}
              style={{ width: "20px", height: "20px", flexShrink: 0 }}
            />
            {showLabel && (
              <span style={{ whiteSpace: "nowrap" }}>{label}</span>
            )}
          </Link>
        );
      })}
    </nav>
  );

  // ── DESKTOP SIDEBAR ──────────────────────────────────────
  if (isDesktop) {
    return (
      <>
        <PortalDropdown />
        <div style={{
          width: open ? `${SIDEBAR_WIDTH}px` : "64px",
          minHeight: "100vh",
          backgroundColor: "#1f2937",
          color: "white",
          display: "flex",
          flexDirection: "column",
          flexShrink: 0,
          transition: "width 0.3s ease",
          overflow: "hidden",
          position: "relative",
          zIndex: 50,
        }}>
          {/* Toggle */}
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: open ? "flex-end" : "center",
            padding: "12px",
            borderBottom: "1px solid #374151",
            minHeight: "49px",
          }}>
            <button
              onClick={() => { setOpen(!open); setDropOpen(false); }}
              style={{
                color: "white",
                fontSize: "20px",
                background: "none",
                border: "none",
                cursor: "pointer",
                lineHeight: 1,
              }}
            >
              {open ? "✕" : "☰"}
            </button>
          </div>

          {open && <AppSwitcherButton />}
          <NavLinks showLabel={open} />
        </div>
      </>
    );
  }

  // ── MOBILE SIDEBAR DRAWER ────────────────────────────────
  return (
    <>
      <PortalDropdown />

      {/* Overlay */}
      {mobileOpen && (
        <div
          onClick={() => { setMobileOpen(false); setDropOpen(false); }}
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 998,
          }}
        />
      )}

      {/* Drawer */}
      <div style={{
        position: "fixed",
        top: 0,
        left: 0,
        height: "100%",
        width: `${SIDEBAR_WIDTH}px`,
        backgroundColor: "#1f2937",
        color: "white",
        display: "flex",
        flexDirection: "column",
        zIndex: 999,
        transform: mobileOpen ? "translateX(0)" : "translateX(-100%)",
        transition: "transform 0.3s ease",
      }}>
        {/* Header */}
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 16px",
          borderBottom: "1px solid #374151",
          minHeight: "49px",
          flexShrink: 0,
        }}>
          <span style={{ fontWeight: 600, fontSize: "16px" }}>Menu</span>
          <button
            onClick={() => { setMobileOpen(false); setDropOpen(false); }}
            style={{
              color: "white",
              fontSize: "20px",
              background: "none",
              border: "none",
              cursor: "pointer",
            }}
          >
            ✕
          </button>
        </div>

        <AppSwitcherButton />

        <div style={{ flex: 1, overflowY: "auto" }}>
          <NavLinks showLabel={true} />
        </div>
      </div>
    </>
  );
};

export default Sidebar;